import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail, MailX } from "lucide-react";

const STATUS_TABS = ["pending", "approved", "rejected", "all"];

const STATUS_BADGE = {
  pending: "bg-amber-500/20 text-amber-400",
  approved: "bg-emerald-500/20 text-emerald-400",
  rejected: "bg-red-500/20 text-red-400",
};

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function OfficeUseForm({ app, onSaved }) {
  const [office, setOffice] = useState(app.office || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/applications/${app.id}`, {
        course_date: office.course_date || null,
        payment: office.payment || "pending",
        registration_no: office.registration_no || "",
        notes: office.notes || "",
      });
      toast.success("Office details saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-3 bg-white/4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--accent))]">For office use</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-white/60">Course date</Label>
          <Input type="date" value={office.course_date || ""} data-testid="office-course-date"
            onChange={(e) => setOffice({ ...office, course_date: e.target.value })}
            className="mt-1 bg-white/5 border-white/15 text-white" />
        </div>
        <div>
          <Label className="text-xs text-white/60">Registration no.</Label>
          <Input value={office.registration_no || ""} data-testid="office-registration-no"
            onChange={(e) => setOffice({ ...office, registration_no: e.target.value })}
            className="mt-1 bg-white/5 border-white/15 text-white" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-white/60">Payment</Label>
        <div className="flex gap-2 mt-1">
          {["pending", "received"].map((p) => (
            <button key={p} type="button" data-testid={`office-payment-${p}`}
              onClick={() => setOffice({ ...office, payment: p })}
              className={`px-3 py-1 rounded-full text-xs font-mono uppercase ${
                office.payment === p ? "bg-[hsl(var(--accent))] text-black" : "bg-white/8 text-white/60"
              }`}>{p}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs text-white/60">Notes</Label>
        <Textarea value={office.notes || ""} rows={2} data-testid="office-notes"
          onChange={(e) => setOffice({ ...office, notes: e.target.value })}
          className="mt-1 bg-white/5 border-white/15 text-white" />
      </div>
      <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="office-save"
        className="border-white/20 text-white bg-transparent hover:bg-white/10">
        {saving ? "Saving..." : "Save office details"}
      </Button>
    </div>
  );
}

function ApplicationDialog({ app, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!window.confirm(`Approve ${app.full_name}? An account will be created and login details emailed to ${app.email}.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/applications/${app.id}/approve`);
      if (r.data.email_sent) {
        toast.success(`Approved — login details emailed to ${app.email}`);
      } else {
        toast.warning(`Approved, but the email FAILED. Temp password: ${r.data.temp_password} — share it manually.`, { duration: 20000 });
      }
      onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!window.confirm(`Reject ${app.full_name}'s application?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/applications/${app.id}/reject`);
      toast.success("Application rejected");
      onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#171512] border-white/10 text-[#F5F1EA] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-3">
            {app.full_name}
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${STATUS_BADGE[app.status]}`}>{app.status}</span>
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Submitted admission application — review details, edit office-use fields, then approve or reject.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Row label="Type" value={app.application_type === "renewal" ? "Renewal" : "First-time training"} />
          <Row label="Preferred course date" value={app.preferred_course_date} />
          <Row label="Date of birth" value={app.date_of_birth} />
          <Row label="Preferred language" value={app.preferred_language} />
          <Row label="Phone / WhatsApp" value={app.phone} />
          <Row label="Email" value={app.email} />
          <Row label="Address" value={app.address} />
          <Row label="Postal code / City" value={`${app.postal_code} ${app.city}`} />
          <Row label="Licence number" value={app.licence_number} />
          <Row label="Country of issue" value={app.licence_country} />
          <Row label="Licence category" value={app.licence_category} />
          <Row label="Licence expiry" value={app.licence_expiry} />
          {app.application_type === "renewal" && (
            <>
              <Row label="TVDE certificate no." value={app.tvde_certificate_number} />
              <Row label="Certificate expiry" value={app.certificate_expiry} />
            </>
          )}
          <Row label="Submitted" value={new Date(app.created_at).toLocaleString()} />
          {app.status === "approved" && (
            <Row label="Credentials email" value={app.email_sent ? "Sent" : "FAILED — send manually"} />
          )}
        </div>

        <OfficeUseForm app={app} onSaved={onChanged} />

        {app.status === "pending" && (
          <div className="flex gap-3 pt-1">
            <Button onClick={approve} disabled={busy} data-testid="application-approve"
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approve &amp; email login details
            </Button>
            <Button onClick={reject} disabled={busy} variant="outline" data-testid="application-reject"
              className="border-red-500/40 text-red-400 bg-transparent hover:bg-red-500/10 rounded-full">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(null);

  const load = (t = tab) =>
    api.get(`/admin/applications${t !== "all" ? `?status_f=${t}` : ""}`).then((r) => setRows(r.data));

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  return (
    <AdminLayout title="Applications">
      <div className="flex gap-2 mb-5">
        {STATUS_TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`applications-tab-${t}`}
            className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${
              tab === t ? "bg-[hsl(var(--accent))] text-black font-medium" : "bg-white/8 text-white/60 hover:bg-white/15"
            }`}>{t}</button>
        ))}
      </div>

      <div className="bg-white/4 border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="applications-table">
          <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/50 font-mono">
            <tr>
              <th className="text-left px-4 py-3">Applicant</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} onClick={() => setSelected(a)} data-testid={`application-row-${a.id}`}
                className="border-t border-white/8 hover:bg-white/5 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="font-medium">{a.full_name}</div>
                  <div className="text-xs text-white/50">{a.email}</div>
                </td>
                <td className="px-4 py-3 text-xs">{a.application_type === "renewal" ? "Renewal" : "First-time"}</td>
                <td className="px-4 py-3 text-xs font-mono">{a.phone}</td>
                <td className="px-4 py-3 text-xs text-white/50 font-mono">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${STATUS_BADGE[a.status]}`}>{a.status}</span>
                </td>
                <td className="px-4 py-3">
                  {a.status === "approved" && (a.email_sent
                    ? <Mail className="w-4 h-4 text-emerald-400" title="Credentials emailed" />
                    : <MailX className="w-4 h-4 text-red-400" title="Email failed" />)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-white/40">No {tab !== "all" ? tab : ""} applications.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <ApplicationDialog app={selected} onClose={() => setSelected(null)} onChanged={() => load()} />
      )}
    </AdminLayout>
  );
}
